import type { User, ApiResponse } from '../types/milestone.types'
import config from '../config/environments'
import { stackService } from './stackService'

/**
 * User Service
 * Handles user profile management operations
 */
export class UserService {
  private static instance: UserService
  private baseUrl: string

  private constructor() {
    // Get base URL with stack-specific base path
    this.baseUrl = stackService.getApiBaseUrl(config.apiBaseUrl)

    // Listen for stack changes and update base URL
    window.addEventListener('stack-changed', () => {
      this.updateBaseUrl()
    })
  }

  /**
   * Update base URL when stack selection changes
   */
  private updateBaseUrl(): void {
    this.baseUrl = stackService.getApiBaseUrl(config.apiBaseUrl)
    console.log('🔄 UserService base URL updated:', this.baseUrl)
  }

  public static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService()
    }
    return UserService.instance
  }

  /**
   * Update user profile
   *
   * @param uid - User ID (from user.id)
   * @param updates - Profile fields to update (userName, phoneNumber, portraitSetting)
   * @returns Updated user data
   */
  public async updateProfile(
    uid: string,
    updates: {
      userName?: string
      phoneNumber?: string
      portraitSetting?: string
    },
    expectedVersion?: number
  ): Promise<ApiResponse<User>> {
    try {
      console.log('📝 Updating user profile:', uid, updates)

      const url = new URL(`${this.baseUrl}/users/${uid}`)
      console.log('🔗 Calling:', url.toString())

      // Include version in body for optimistic locking (backend extracts it)
      const body = expectedVersion !== undefined
        ? { ...updates, version: expectedVersion }
        : updates

      const response = await fetch(url.toString(), {
        method: 'PUT',
        credentials: 'include', // Send cookies for auth
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      })

      console.log('📡 PUT /users response status:', response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ Profile update failed:', response.status, errorText)
        return {
          success: false,
          data: null as any,
          message: `Profile update failed: ${response.status}`,
          errors: [{
            code: 'PROFILE_UPDATE_FAILED',
            message: errorText || `HTTP ${response.status}`
          }]
        }
      }

      const userData = await response.json()
      console.log('✅ Profile updated successfully:', userData)

      // Transform backend response to User type (similar to authService.getUserInfo)
      const user: User = {
        id: userData.id,
        email: userData.email || '',
        name: userData.userName || this.extractDisplayNameFromEmail(userData.email || ''),
        organizations: [
          {
            id: userData.orgId || 'unknown',
            name: userData.orgId || 'Unknown Org',
            role: userData.isOrgOwner ? 'admin' as const : 'member' as const,
            permissions: userData.isOrgOwner ? ['read', 'write', 'admin'] : ['read', 'write']
          }
        ],
        currentOrganization: {
          id: userData.orgId || 'unknown',
          name: userData.orgId || 'Unknown Org',
          role: userData.isOrgOwner ? 'admin' as const : 'member' as const,
          permissions: userData.isOrgOwner ? ['read', 'write', 'admin'] : ['read', 'write']
        },
        tokenExpiry: userData.tokenExpiry,
        userName: userData.userName,
        phoneNumber: userData.phoneNumber,
        portraitSetting: userData.portraitSetting
      }

      return {
        success: true,
        data: user,
        message: 'Profile updated successfully'
      }
    } catch (error: any) {
      console.error('❌ Profile update error:', error)
      return {
        success: false,
        data: null as any,
        message: 'Profile update failed',
        errors: [{
          code: error.name || 'PROFILE_UPDATE_ERROR',
          message: error.message || 'Failed to update profile'
        }]
      }
    }
  }

  // Helper method to extract display name from email
  private extractDisplayNameFromEmail(email: string): string {
    if (!email) return 'User'

    const emailPrefix = email.split('@')[0]

    if (emailPrefix.includes('.')) {
      const firstName = emailPrefix.split('.')[0]
      return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
    } else if (emailPrefix.includes('_')) {
      const firstName = emailPrefix.split('_')[0]
      return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
    } else if (emailPrefix.includes('-')) {
      const firstName = emailPrefix.split('-')[0]
      return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
    } else {
      return emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1).toLowerCase()
    }
  }
}

// Export singleton instance
export const userService = UserService.getInstance()
export default userService
